import { useEffect, useRef, useState } from 'react';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { Terminal as XTerm } from '@xterm/xterm';
import type { User } from 'firebase/auth';
import { buildTerminalUrl } from './terminal-url';

type TerminalProps = {
  sessionId: string;
  user: User;
  active: boolean;
};

type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

export function Terminal({ sessionId, user, active }: TerminalProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const writableRef = useRef(false);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [writable, setWritable] = useState(false);

  useEffect(() => {
    if (!active || !hostRef.current) return;

    const terminal = new XTerm({
      allowProposedApi: false,
      cursorBlink: true,
      cursorStyle: 'bar',
      fontFamily: '"DM Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 13.5,
      fontWeight: '400',
      lineHeight: 1.28,
      scrollback: 20_000,
      theme: {
        background: '#ffffff',
        foreground: '#263245',
        cursor: '#f97316',
        cursorAccent: '#ffffff',
        selectionBackground: '#fed7aa99',
        black: '#273244',
        red: '#dc2626',
        green: '#15803d',
        yellow: '#a16207',
        blue: '#2563eb',
        magenta: '#7c3aed',
        cyan: '#0f766e',
        white: '#64748b',
        brightBlack: '#7b8799',
        brightWhite: '#0f172a',
      },
    });
    const fit = new FitAddon();
    terminal.loadAddon(fit);
    terminal.loadAddon(new WebLinksAddon((_event, uri) => window.open(uri, '_blank', 'noopener')));
    terminal.open(hostRef.current);
    fit.fit();
    terminal.writeln('\x1b[38;2;161;161;170mConnecting to the DevPilot session…\x1b[0m');

    let socket: WebSocket | null = null;
    let closed = false;
    let reconnectTimer: number | undefined;
    let reconnectAttempt = 0;
    const clientId = crypto.randomUUID();
    const gateway =
      import.meta.env.VITE_TERMINAL_WS_URL ?? 'wss://terminal.devpilot.yansoisson.com/ws';

    const sendResize = () => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'resize', cols: terminal.cols, rows: terminal.rows }));
      }
    };

    const connect = async () => {
      setConnection('connecting');
      try {
        const token = await user.getIdToken();
        socket = new WebSocket(buildTerminalUrl(gateway, sessionId, clientId));
        socketRef.current = socket;
        socket.addEventListener('open', () => {
          socket?.send(JSON.stringify({ type: 'authenticate', token }));
        });
        socket.addEventListener('message', (event) => {
          try {
            const message = JSON.parse(String(event.data)) as {
              type: string;
              data?: string;
              status?: string;
              writable?: boolean;
              message?: string;
            };
            if (message.type === 'output' && message.data) terminal.write(message.data);
            if (message.type === 'status') {
              setConnection(message.status === 'connected' ? 'connected' : 'disconnected');
              writableRef.current = Boolean(message.writable);
              setWritable(writableRef.current);
              reconnectAttempt = 0;
              sendResize();
            }
            if (message.type === 'error') {
              terminal.writeln(`\r\n\x1b[31m${message.message ?? 'Terminal error'}\x1b[0m`);
              setConnection('error');
            }
          } catch {
            terminal.write(String(event.data));
          }
        });
        socket.addEventListener('close', () => {
          if (closed) return;
          setConnection('disconnected');
          setWritable(false);
          writableRef.current = false;
          const delay = Math.min(10_000, 750 * 2 ** reconnectAttempt++);
          reconnectTimer = window.setTimeout(connect, delay);
        });
        socket.addEventListener('error', () => setConnection('error'));
      } catch (error) {
        terminal.writeln(`\r\n\x1b[31m${String(error)}\x1b[0m`);
        setConnection('error');
      }
    };

    const input = terminal.onData((data) => {
      if (socket?.readyState === WebSocket.OPEN && writableRef.current) {
        socket.send(JSON.stringify({ type: 'input', data }));
      }
    });
    const resize = terminal.onResize(sendResize);
    const observer = new ResizeObserver(() => {
      fit.fit();
      sendResize();
    });
    observer.observe(hostRef.current);
    void connect();

    return () => {
      closed = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      observer.disconnect();
      input.dispose();
      resize.dispose();
      socket?.close();
      socketRef.current = null;
      terminal.dispose();
    };
  }, [active, sessionId, user]);

  const takeControl = () => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: 'take-control' }));
    }
  };

  return (
    <div className="terminal-shell">
      <div className="terminal-statusbar">
        <div className="terminal-lights" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <span className={`connection-label ${connection}`}>
          <i />{' '}
          {connection === 'connected'
            ? writable
              ? 'Live · Control'
              : 'Live · Watching'
            : connection}
        </span>
        {!writable && connection === 'connected' ? (
          <button type="button" className="terminal-control-button" onClick={takeControl}>
            Take control
          </button>
        ) : null}
      </div>
      <div ref={hostRef} className="terminal-host" aria-label="Session terminal" />
    </div>
  );
}
